import {
  forwardRef,
  InputHTMLAttributes,
  useImperativeHandle,
  useRef,
} from 'react';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/icons/DatePicker';
import styles from './input.module.css';

export const inputClasses = cn(
  'flex items-center self-stretch h-9 w-full rounded border border-blue-900 bg-white px-4 py-3 text-md text-neutral-800',
  'hover:border-2 hover:border-blue-700',
  'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-blue-900',
  'placeholder:text-neutral-800 autofill:text-neutral-800 autofill:bg-white target:text-neutral-800 target:bg-white',
  'focus:outline-hidden focus-visible:outline-hidden focus-visible:border-shadow-glow focus-visible:shadow-ring focus-visible:bg-white',
  'invalid:border-2 invalid:border-error aria-invalid:border-2 aria-invalid:border-error',
  'disabled:border-neutral-400 disabled:border disabled:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-50',
  'aria-disabled:border-neutral-400 aria-disabled:border aria-disabled:bg-neutral-200 aria-disabled:cursor-not-allowed aria-disabled:opacity-50'
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  asDiv?: boolean;
  focused?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, placeholder, asDiv = false, focused, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);
    let defaultPlaceholder;

    switch (type) {
      case 'date':
        defaultPlaceholder = placeholder || 'MM/DD/YYYY';
        break;
      case 'month':
        defaultPlaceholder = placeholder || 'MM/YYYY';
        break;
      default:
        defaultPlaceholder = placeholder;
        break;
    }

    /**
     * Handles input changes for date and month input types.
     * Ensures that the input value is properly formatted by trimming the year to 4 characters
     * and maintaining the correct structure for the specified type.
     *
     * @param event - The change event triggered by the input element.
     * @param event.target.value - The current value of the input element.
     *
     * Behavior:
     * - If the input type is not 'date' or 'month', the function exits early.
     * - Splits the input value by the '-' character to extract its parts.
     * - For 'date' type:
     *   - Ensures the value has exactly 3 parts (year, month, day).
     * - For 'month' type:
     *   - Ensures the value has exactly 2 parts (year, month).
     * - Trims the year part to a maximum of 4 characters.
     * - Reconstructs the value and updates the input element's value.
     */
    const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (type !== 'date' && type !== 'month') return;

      const value = event.target.value;
      const parts = value.split('-');

      if (
        type === 'date' &&
        parts.length !== 3 &&
        type !== 'month' &&
        parts.length === 2
      )
        return;

      parts[0] = parts[0].slice(0, 4); // Trim the year to 4 characters
      event.target.value = parts.join('-');
    };

    /**
     * Standard inputs default to input elements
     * Microform inputs are rendered as both:
     * hidden inputs and visible divs.
     * The divs need the matching id for the microform script to target
     */
    const InputComponent = asDiv ? 'div' : 'input';

    return (
      <div className="relative">
        <InputComponent
          type={type}
          className={cn(
            inputClasses,
            // method of adding focus styles for microform div
            focused && 'outline-hidden border-azure-500 shadow-ring border-2',
            // applies styles for datepicker icon
            styles.input,
            className
          )}
          placeholder={defaultPlaceholder}
          ref={inputRef}
          onInput={handleInput}
          {...props}
        />

        {(type === 'date' || type === 'month') && (
          <button
            type="button"
            className={cn(
              'absolute right-2 top-1/2 transform -translate-y-1/2 w-7 h-7',
              'bg-none border-none p-0 cursor-pointer pointer-events-auto',
              'focus:outline-hidden focus-visible:shadow-ring focus-visible:outline-hidden focus-visible::border-2 focus-visible::border-azure-700'
            )}
            onClick={() => {
              // Trigger the native date picker
              inputRef.current?.showPicker();
            }}
          >
            <DatePicker className="w-full h-full pointer-events-none" />
          </button>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
